import { useState, useEffect } from "react";
import { fetchLanguages, fetchCountries, submitDemographics } from "../service/api";
import PageWrapper from "./PageWrapper";

const DemographicsForm = ({ userId, participantSource = "direct", onNext, onBack }) => {
  const isProlific = participantSource === "prolific";
  const initialDemographics = JSON.parse(localStorage.getItem("demographicsData")) || {
    yearOfBirth: "",
    gender: "",
    country: "",
    language: "",
    education: "",
    watchFrequency: "",
    streamingServices: [],
    engagement_reviews: "",
    engagement_ratings: "",
    engagement_news: "",
    engagement_discussions: ""
  };

  const [formData, setFormData] = useState(initialDemographics);
  const [otherStreaming, setOtherStreaming] = useState(initialDemographics.otherStreaming || "");
  const [error, setError] = useState("");
  const [languages, setLanguages] = useState([]);
  const [filteredLanguages, setFilteredLanguages] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(
    initialDemographics.countrySelectionConfirmed ? initialDemographics.country : null
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    initialDemographics.languageSelectionConfirmed ? initialDemographics.language : null
  );
  const [tooltip, setTooltip] = useState(null);

  const streamingOptions = [
    "Netflix", "Amazon Prime Video", "Hulu", "Disney+", "HBO Max",
    "SkyShowtime", "Other", "None"
  ];

  // ✅ Save form to localStorage
  useEffect(() => {
    localStorage.setItem("demographicsData", JSON.stringify({
      ...formData,
      otherStreaming,
      countrySelectionConfirmed: selectedCountry === formData.country,
      languageSelectionConfirmed: selectedLanguage === formData.language,
    }));
  }, [formData, otherStreaming, selectedCountry, selectedLanguage]);

  // ✅ Set section start time
  useEffect(() => {
    if (!localStorage.getItem("startTime_demographics")) {
      localStorage.setItem("startTime_demographics", Date.now().toString());
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (isProlific) return;
    const loadLanguages = async () => {
      try {
        const langs = await fetchLanguages();
        setLanguages(langs);
      } catch (err) {
        console.error("Failed to load languages", err);
      }
    };
    loadLanguages();
  }, [isProlific]);

  useEffect(() => {
    if (isProlific) return;
    const loadCountries = async () => {
      try {
        const countriesList = await fetchCountries();
        setCountries(countriesList);
      } catch (err) {
        console.error("Failed to load countries", err);
      }
    };
    loadCountries();
  }, [isProlific]);

  // A participant can start typing before the asynchronous reference data has
  // loaded. Recompute when the options arrive so that early input still opens
  // the dropdown instead of remaining stuck with an empty filtered list.
  useEffect(() => {
    const value = formData.country;
    setFilteredCountries(value && value !== selectedCountry
      ? countries.filter((country) => country.toLowerCase().startsWith(value.toLowerCase()))
      : []);
  }, [countries, formData.country, selectedCountry]);

  useEffect(() => {
    const value = formData.language;
    setFilteredLanguages(value && value !== selectedLanguage
      ? languages.filter((language) => language.toLowerCase().startsWith(value.toLowerCase()))
      : []);
  }, [languages, formData.language, selectedLanguage]);

  const handleLanguageChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, language: value });
    if (value !== selectedLanguage) setSelectedLanguage(null);

    if (value.length > 0) {
      setFilteredLanguages(languages.filter(lang =>
        lang.toLowerCase().startsWith(value.toLowerCase())
      ));
    } else {
      setFilteredLanguages([]);
    }
  };

  const handleCountryChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, country: value });
    if (value !== selectedCountry) setSelectedCountry(null);

    if (value.length > 0) {
      setFilteredCountries(countries.filter(country =>
        country.toLowerCase().startsWith(value.toLowerCase())
      ));
    } else {
      setFilteredCountries([]);
    }
  };

  const handleStreamingChange = (option) => {
    let updatedServices = [...formData.streamingServices];

    if (option === "None") {
      updatedServices = ["None"];
    } else {
      updatedServices = updatedServices.includes(option)
        ? updatedServices.filter(item => item !== option)
        : [...updatedServices, option];

      updatedServices = updatedServices.filter(item => item !== "None");
    }

    setFormData({ ...formData, streamingServices: updatedServices });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userId) {
      setError("⚠️ Please complete all required questions before proceeding.");
      return;
    }

    if (!isProlific) {
      const year = parseInt(formData.yearOfBirth, 10);
      const currentYear = new Date().getFullYear();
      if (isNaN(year) || year < 1900 || year > currentYear) {
        setError(`⚠️ Please enter a valid year of birth (1900–${currentYear}).`);
        return;
      }
    }

    const requiredFields = [
      ...(!isProlific ? [formData.gender, formData.country, formData.language] : []),
      formData.education,
      formData.watchFrequency,
      formData.watchDuration,
      formData.engagement_reviews,
      formData.engagement_ratings,
      formData.engagement_news,
      formData.engagement_discussions
    ];

    const educationOther = typeof formData.educationOther === "string"
      ? formData.educationOther.trim()
      : "";
    const normalizedOtherStreaming = typeof otherStreaming === "string"
      ? otherStreaming.trim()
      : "";
    const hasValidCountrySelection = isProlific
      || (selectedCountry === formData.country && countries.includes(selectedCountry));
    const hasValidLanguageSelection = isProlific
      || (selectedLanguage === formData.language && languages.includes(selectedLanguage));
    if (
      requiredFields.some((value) => typeof value !== "string" || !value.trim())
      || !hasValidCountrySelection
      || !hasValidLanguageSelection
      || formData.streamingServices.length === 0
      || (formData.education === "Other" && !educationOther)
      || (formData.streamingServices.includes("Other") && !normalizedOtherStreaming)
    ) {
      setError("⚠️ Please complete all required questions before proceeding.");
      return;
    }

    const startTime = parseInt(localStorage.getItem("startTime_demographics"), 10);
    const endTime = Date.now();
    const elapsedSeconds = Math.round((endTime - startTime) / 1000);
    const timestamp = new Date().toISOString();

    const dataToSend = {
      userId,
      ...(!isProlific ? {
        yearOfBirth: formData.yearOfBirth,
        gender: formData.gender,
        country: formData.country,
        language: formData.language,
      } : {}),
      education: formData.education,
      ...(formData.education === "Other"
        ? { educationOther }
        : {}),
      watchFrequency: formData.watchFrequency,
      watchDuration: formData.watchDuration,
      streamingServices: formData.streamingServices.includes("Other")
        ? [...formData.streamingServices, `Other: ${normalizedOtherStreaming}`]
        : formData.streamingServices,
      engagement_reviews: formData.engagement_reviews,
      engagement_ratings: formData.engagement_ratings,
      engagement_news: formData.engagement_news,
      engagement_discussions: formData.engagement_discussions,
      timestamp,
      elapsedSeconds
    };

    try {
      await submitDemographics(dataToSend);
      localStorage.setItem("currentStep", "movies");
      onNext();
    } catch (err) {
      console.error("❌ Error submitting demographics:", err);
      setError("❌ Could not save your answers. Please try again.");
    }
  };

  return (
    <PageWrapper>
      <h2 style={{ textAlign: "center" }}>
        {isProlific ? "Background & Watching Habits" : "📋 Demographics & Watching Habits"}
      </h2>
      <p style={{ textAlign: "center" }}>We begin with a few background questions. Please answer the following:</p>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "25px" }}>
        {!isProlific && (
          <>
        {/* Year of Birth Input */}
        <label><b>Year of Birth:</b></label>
        <input
          type="text"
          name="yearOfBirth"
          value={formData.yearOfBirth}
          onChange={(e) => {
            let value = e.target.value.replace(/\D/g, "");
            value = value.slice(0, 4); // Limit to 4 digits

            // Always update input so user can type freely
            setFormData({ ...formData, yearOfBirth: value });
          }}
          onKeyDown={(e) => {
            if (["e", "E", "+", "-"].includes(e.key)) {
              e.preventDefault();
            }
          }}
          placeholder="Enter a 4-digit year of birth (e.g., 1997)"
          maxLength="4"
          required
          style={{ width: "350px", padding: "5px" }}
        />

        {/* Gender Dropdown */}
        <label htmlFor="gender"><b>Gender:</b></label>
        <select id="gender" name="gender" value={formData.gender} onChange={handleChange} required style={{ padding: "5px" }}>
          <option value="">Select...</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Non-binary">Non-binary</option>
          <option value="Prefer not to say">Prefer not to say</option>
          <option value="Other">Other</option>
        </select>

        {/* Country Input with Auto-suggestions */}
        <label htmlFor="country"><b>Country of residence:</b></label>
        <input
          id="country"
          type="text"
          name="country"
          value={formData.country}
          onChange={handleCountryChange}
          placeholder="Start typing..."
          required
          autoComplete="off"
          aria-autocomplete="list"
          aria-controls="country-suggestions"
          style={{ padding: "5px", width: "100%" }}
        />
        {filteredCountries.length > 0 && (
          <ul id="country-suggestions" role="listbox" style={{ position: "relative", zIndex: 10, listStyle: "none", padding: 0, margin: "0", maxHeight: "150px", overflowY: "auto", border: "1px solid var(--color-border)", background: "white" }}>
            {filteredCountries.slice(0, 10).map((country) => (
              <li key={country} role="option" aria-selected={selectedCountry === country}>
                <button type="button" style={{ width: "100%", padding: "5px", cursor: "pointer", textAlign: "left", border: 0, borderBottom: "1px solid var(--color-border)", background: "white" }}
                  onClick={() => {
                    setFormData({ ...formData, country });
                    setSelectedCountry(country);
                    setFilteredCountries([]);
                  }}>
                  {country}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Language Input with Auto-suggestions */}
        <label htmlFor="language"><b>Primary Language:</b></label>
        <input
          id="language"
          type="text"
          name="language"
          value={formData.language}
          onChange={handleLanguageChange}
          placeholder="Start typing..."
          required
          autoComplete="off"
          aria-autocomplete="list"
          aria-controls="language-suggestions"
          style={{ padding: "5px", width: "100%" }}
        />
        {filteredLanguages.length > 0 && (
          <ul id="language-suggestions" role="listbox" style={{ position: "relative", zIndex: 10, listStyle: "none", padding: 0, margin: "0", maxHeight: "150px", overflowY: "auto", border: "1px solid var(--color-border)", background: "white" }}>
            {filteredLanguages.slice(0, 10).map((lang) => (
              <li key={lang} role="option" aria-selected={selectedLanguage === lang}>
                <button type="button" style={{ width: "100%", padding: "5px", cursor: "pointer", textAlign: "left", border: 0, borderBottom: "1px solid var(--color-border)", background: "white" }}
                  onClick={() => {
                    setFormData({ ...formData, language: lang });
                    setSelectedLanguage(lang);
                    setFilteredLanguages([]);
                  }}>
                  {lang}
                </button>
              </li>
            ))}
          </ul>


        )}
          </>
        )}

        {/* Education Input */}
        {/* <label><b>Years of Formal Education:</b></label>
                <p style={{ fontSize: "small", fontStyle: "italic" }}>
                    (Ended with a certificate or diploma only: e.g., 8 years of primary school + 4 years of high school = 12 years)
                </p> */}
        <label htmlFor="education"><b>Highest Obtained Education:</b></label>
        <select
          id="education"
          name="education"
          value={formData.education}
          onChange={handleChange}
          required
          style={{ width: "250px", padding: "5px" }}
        >
          <option value="" disabled>Select your education level</option>
          <option value="No formal education">No formal education</option>
          <option value="Primary school">Primary school</option>
          <option value="High school diploma">High school diploma</option>
          <option value="Associate degree">Associate degree</option>
          <option value="Bachelor's degree">Bachelor's degree</option>
          <option value="Master's degree">Master's degree</option>
          <option value="Doctorate">Doctorate</option>
          <option value="Other">Other</option>
        </select>

        {/* Inline input for 'Other' option, styled like the streaming input */}
        {formData.education === "Other" && (
          <input
            type="text"
            name="educationOther"
            value={formData.educationOther || ""}
            onChange={(e) => setFormData({ ...formData, educationOther: e.target.value })}
            placeholder="Specify other type of education..."
            required
            style={{ width: "250px", padding: "5px", marginTop: "8px" }}
          />
        )}

        {/* Watching Frequency */}
        <label style={{ marginTop: "15px" }}><b>How often do you usually watch movies or TV series with your full attention?</b></label>
        {[
          "Very often - almost every day (6-7 days/week)", //, ~100%
          "Often - about every second day (3-4 days/week)",//, ~50%
          "Occasionally - once every few days (1-2 days/week)",//, ~20%
          "Rarely - once every 1-2 weeks",// (~10%)
          "Almost never - less than once per month"//(~0-5%)
        ].map((option) => (
          <label key={option} style={{ display: "block", marginBottom: "5px" }}>
            <input
              type="radio"
              name="watchFrequency"
              value={option}
              checked={formData.watchFrequency === option}
              onChange={handleChange}
              required
            />
            {option}
          </label>
        ))}

        <br />

        {/* Watching Duration */}
        <label style={{ marginTop: "15px" }}><b>On the days you watch, how much time do you usually spend watching?</b></label>
        {[
          "Less than 1 hour",
          "1-2 hours",
          "2-3 hours",
          "3-4 hours",
          "More than 4 hours"
        ].map((option) => (
          <label key={option} style={{ display: "block", marginBottom: "5px" }}>
            <input
              type="radio"
              name="watchDuration"
              value={option}
              checked={formData.watchDuration === option}
              onChange={handleChange}
              required
            />
            {option}
          </label>
        ))}

        {/* Streaming Services */}
        <label><b>Which streaming services do you use? (Select all that apply)</b></label>
        {streamingOptions.map((option) => (
          <label key={option}>
            <input type="checkbox" value={option} checked={formData.streamingServices.includes(option)} onChange={() => handleStreamingChange(option)} />
            {option}
          </label>
        ))}

        {/* "Other" Streaming Service Input */}
        {formData.streamingServices.includes("Other") && (
          <input
            type="text"
            name="otherStreaming"
            placeholder="Specify other streaming service..."
            value={otherStreaming}
            onChange={(e) => setOtherStreaming(e.target.value)}
            required
          />
        )}

        {/* Engagement with Movie Content */}
        {/* <label style={{ fontWeight: "bold", marginBottom: "10px", display: "block" }}>
                  How often do you engage with the following?
                </label>

                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px" }}>
                  <thead>
                  <tr>
                    <th style={{ textAlign: "left" }}></th>
                    {[
                      { label: "Never", explanation: "Almost never - close to 0% of the time" },
                      { label: "Rarely", explanation: "About 10% of the time - about 1 in 10 occasions" },
                      { label: "Occasionally", explanation: "About  20% of the time - about 1 in 5 occasions" },
                      { label: "Often", explanation: "About  50% of the time - about every second time you engage" },
                      { label: "Very Often", explanation: "Almost always - close to 100% of the time" }
                      // { label: "Never", explanation: "Almost never (close to 0%)" },
                      // { label: "Rarely", explanation: "Around 1 out of 10 times (10%)" },
                      // { label: "Occasionally", explanation: "Around 1 out of 5 times (20%)" },
                      // { label: "Often", explanation: "Around half the time (50%)" },
                      // { label: "Very Often", explanation: "Almost every time (close to 100%)" }
                    ].map(({ label, explanation }) => (
                      <th key={label} style={{ padding: "5px", textAlign: "center" }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          {label}
                          <span
                            title={explanation}
                            style={{
                              fontSize: "var(--font-size-xs)",
                              color: "var(--color-info)",
                              cursor: "help",
                              borderBottom: "1px dotted var(--color-info)"
                            }}
                          >
                            ℹ️
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                  <tbody>
                    {[
                      { key: "reviews", label: "Read movie reviews" },
                      { key: "ratings", label: "Check movie ratings" },
                      { key: "news", label: "Follow movie news" },
                      { key: "discussions", label: "Participate in online discussions" }
                    ].map(({ key, label }) => (
                      <tr key={key}>
                        <td style={{ fontWeight: "bold", padding: "6px 0" }}>{label}</td>
                        {[
                          "Never",
                          "Rarely",
                          "Occasionally",
                          "Often",
                          "Very Often"
                        ].map((level) => (
                          <td key={level} style={{ textAlign: "center", padding: "4px 0" }}>
                            <input
                              type="radio"
                              name={`engagement_${key}`}
                              value={level}
                              checked={formData[`engagement_${key}`] === level}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  [`engagement_${key}`]: e.target.value
                                })
                              }
                              required
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table> */}

        {/* Engagement with Movie Content */}
        <label style={{ fontWeight: "bold", marginBottom: "10px", display: "block" }}>
          How often do you engage with the following?
        </label>

        <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Activity</th>
              {[
                { label: "Never", explanation: "Almost never - close to 0% of the time" },
                { label: "Rarely", explanation: "About 10% of the time - about 1 in 10 occasions" },
                { label: "Occasionally", explanation: "About 20% of the time - about 1 in 5 occasions" },
                { label: "Often", explanation: "About 50% of the time - about every second time you engage" },
                { label: "Very Often", explanation: "Almost always - close to 100% of the time" }
              ].map(({ label, explanation }) => (
                <th key={label} style={{ padding: "5px", textAlign: "center", position: "relative" }}>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: "4px", position: "relative" }}>
                    {label}
                    <div style={{ position: "relative" }}>
                      <span
                        style={{
                          fontSize: "var(--font-size-base)",
                          color: "var(--color-info)",
                          cursor: "pointer",
                          borderBottom: "1px dotted var(--color-info)",
                          position: "relative"
                        }}
                        onMouseEnter={() => setTooltip(label)}
                        onMouseLeave={() => setTooltip(null)}
                      >
                        ℹ️
                      </span>

                      {tooltip === label && (
                        <div
                          style={{
                            position: "absolute",
                            top: "100%",
                            left: "50%",
                            transform: "translateX(-50%)",
                            backgroundColor: "var(--color-background-secondary)",
                            border: "1px solid var(--color-border)",
                            borderRadius: "4px",
                            padding: "10px",
                            fontSize: "var(--font-size-base)",
                            whiteSpace: "normal",
                            zIndex: 1000,
                            width: "220px",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.1)"
                          }}
                        >
                          {explanation}
                        </div>
                      )}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {[
              { key: "reviews", label: "Read movie reviews" },
              { key: "ratings", label: "Check movie ratings" },
              { key: "news", label: "Follow movie news" },
              { key: "discussions", label: "Participate in online discussions" }
            ].map(({ key, label }) => (
              <tr key={key}>
                <td style={{ fontWeight: "bold", padding: "6px 0" }}>{label}</td>
                {["Never", "Rarely", "Occasionally", "Often", "Very Often"].map((level) => (
                  <td key={level} style={{ textAlign: "center", padding: "4px 0" }}>
                    <input
                      type="radio"
                      name={`engagement_${key}`}
                      value={level}
                      checked={formData[`engagement_${key}`] === level}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          [`engagement_${key}`]: e.target.value
                        })
                      }
                      required
                      aria-label={`${label} - ${level}`}
                      style={{
                        transform: "scale(1.5)",
                        margin: "5px"
                      }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {/* Error Message */}
        {error && <p style={{ color: "var(--color-error)" }} role="alert" aria-live="polite">{error}</p>}

        {/* Navigation Buttons */}
        <div style={{ marginTop: "20px", display: "flex", justifyContent: "space-between" }}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              style={{
                padding: "10px",
                fontSize: "var(--font-size-base)",
                backgroundColor: "var(--color-disabled)",
                color: "white",
                border: "none",
                cursor: "pointer"
              }}
            >
              ⬅ Back
            </button>
          )}

          <button
            type="submit"
            style={{
              padding: "10px",
              fontSize: "var(--font-size-base)",
              backgroundColor: "var(--color-success)",
              color: "white",
              border: "none",
              cursor: "pointer"
            }}
          >
            Submit & Proceed ➡
          </button>
        </div>
      </form>
    </PageWrapper>
  );
};

export default DemographicsForm;
